/********1st-optimised-new-code******************************************************************************************************************************************************************************************************************* */

import React, {
  useState,
  useEffect,
  Suspense,
  lazy,
  useCallback,
  useMemo,
} from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  useLocation,
  Navigate,
  Outlet,
} from "react-router-dom";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import {
  generateEventId,
  initMetaPixel,
  trackPageView,
  trackStandardEvent,
} from "./lib/meta";

import useBalanceRealtime from "./hooks/useBalanceRealtime";

import { fetchJackpotPool } from "./features/roundSlice";
import { logout, performLogout, restoreSession } from "./features/authSlice";

import Header from "./components/Header";
import ProtectedRoute from "./components/ProtectedRoute";
import ToastContainerWrapper from "./components/ToastContainerWrapper";
import LoadingSpinner from "./components/LoadingSpinner";
import LogoutConfirmModal from "./components/LogoutConfirmModal";

import HowToPlay from "./components/HowToPlay";
import Faqs from "./components/Faqs";
import TermsOfService from "./components/TermsOfService";
import PrivacyPolicy from "./components/PrivacyPolicy";
import ResponsibleGaming from "./components/ResponsibleGaming";

import {
  useDepositModal,
  useShowLogin,
  useShowRegister,
} from "./Context/modalContex";

import GameLayout from "./components/GameLayout";
import Footer from "./components/Footer";

// Lazy-loaded Route Components
const GameRoom = lazy(() => import("./components/GameRoom"));
const Profile = lazy(() => import("./components/Profile"));
const Login = lazy(() => import("./components/Login"));
const Register = lazy(() => import("./components/Register"));
const ReferalRegister = lazy(() => import("./components/ReferalRegister"));

// Lazy-loaded Modal Components
const DepositModal = lazy(() => import("./components/DepositModal"));
const WithdrawModal = lazy(() => import("./components/WithdrawModal"));

const NavigationHandler = React.memo(function NavigationHandler({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const isAuthenticated = useSelector(
    (state) => state.auth.authStatus === "authenticated",
    shallowEqual,
  );

  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const shouldInterceptBack = useMemo(() => {
    return (
      isAuthenticated &&
      (location.pathname === "/profile" || location.pathname.includes("/game/"))
    );
  }, [isAuthenticated, location.pathname]);

  useEffect(() => {
    const handlePopState = (event) => {
      if (shouldInterceptBack && !event.state?.bypass) {
        window.history.pushState(null, "", location.pathname);
        setShowLogoutConfirm(true);
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [shouldInterceptBack, location.pathname]);

  const handleConfirmLogout = useCallback(async () => {
    try {
      await dispatch(performLogout()).unwrap();
    } catch {
      // Local logout still proceeds if the API is temporarily unavailable.
    }
    dispatch(logout());
    setShowLogoutConfirm(false);
    navigate("/", { state: { bypass: true }, replace: true });
  }, [dispatch, navigate]);

  const handleCancelLogout = useCallback(() => {
    setShowLogoutConfirm(false);
    window.history.pushState(null, "", location.pathname);
  }, [location.pathname]);

  return (
    <>
      {children}
      {showLogoutConfirm && (
        <LogoutConfirmModal
          isOpen={showLogoutConfirm}
          onConfirm={handleConfirmLogout}
          onCancel={handleCancelLogout}
        />
      )}
    </>
  );
});

const MetaPageTracker = React.memo(function MetaPageTracker() {
  const location = useLocation();

  const viewContentConfig = useMemo(() => {
    const routeMap = {
      "/": {
        contentName: "coin_game_lobby",
        contentCategory: "game",
      },
      "/register": {
        contentName: "registration_page",
        contentCategory: "registration",
      },
      "/how": {
        contentName: "how_to_play",
        contentCategory: "education",
      },
      "/faqs": {
        contentName: "faq_page",
        contentCategory: "support",
      },
      "/terms": {
        contentName: "terms_of_service",
        contentCategory: "legal",
      },
      "/privacy": {
        contentName: "privacy_policy",
        contentCategory: "legal",
      },
      "/responsible-gaming": {
        contentName: "responsible_gaming",
        contentCategory: "compliance",
      },
      "/profile": {
        contentName: "player_profile",
        contentCategory: "account",
      },
    };

    return routeMap[location.pathname] || null;
  }, [location.pathname]);

  useEffect(() => {
    initMetaPixel();
  }, []);

  useEffect(() => {
    trackPageView();
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!viewContentConfig) {
      return;
    }

    trackStandardEvent(
      "ViewContent",
      {
        content_name: viewContentConfig.contentName,
        content_category: viewContentConfig.contentCategory,
      },
      generateEventId("view_content"),
    );
  }, [viewContentConfig]);

  return null;
});

const AppChrome = React.memo(function AppChrome({
  onDepositOpen,
  onWithdrawOpen,
}) {
  return (
    <>
      <Header
        onDepositOpen={onDepositOpen}
        onWithdrawOpen={onWithdrawOpen}
      />

      <main className="container md:max-w-full mx-auto p-1 mt-20 md:mt-0 md:p-6">
        <Suspense fallback={<LoadingSpinner />}>
          <Outlet />
        </Suspense>
      </main>

      <Footer />
    </>
  );
});

function App() {
  const dispatch = useDispatch();

  useBalanceRealtime();

  const { showLogin } = useShowLogin();
  const { showRegister } = useShowRegister();
  const { showDepositModal, setShowDepositModal } = useDepositModal();

  const [isWithdrawOpen, setWithdrawOpen] = useState(false);

  useEffect(() => {
    dispatch(restoreSession());
    dispatch(fetchJackpotPool());
  }, [dispatch]);

  const openDepositModal = useCallback(() => {
    setShowDepositModal(true);
  }, [setShowDepositModal]);

  const closeDepositModal = useCallback(() => {
    setShowDepositModal(false);
  }, [setShowDepositModal]);

  const openWithdrawModal = useCallback(() => {
    setWithdrawOpen(true);
  }, []);

  const closeWithdrawModal = useCallback(() => {
    setWithdrawOpen(false);
  }, []);

  return (
    <Router>
      <NavigationHandler>
        <MetaPageTracker />
        <ToastContainerWrapper />

        <div className="min-h-screen bg-[#071129] text-gray-100">
          <Routes>
            <Route
              path="/"
              element={<GameLayout />}
            >
              <Route
                element={
                  <AppChrome
                    onDepositOpen={openDepositModal}
                    onWithdrawOpen={openWithdrawModal}
                  />
                }
              >
                <Route index element={<GameRoom />} />
                <Route path="register" element={<ReferalRegister />} />
                <Route path="how" element={<HowToPlay />} />
                <Route path="faqs" element={<Faqs />} />
                <Route path="terms" element={<TermsOfService />} />
                <Route path="privacy" element={<PrivacyPolicy />} />
                <Route
                  path="responsible-gaming"
                  element={<ResponsibleGaming />}
                />
                <Route
                  path="profile"
                  element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Route>
          </Routes>

          <Suspense fallback={<LoadingSpinner />}>
            <AnimatePresence mode="wait">
              {showDepositModal && (
                <motion.div
                  key="deposit-modal"
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 20 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  className="fixed inset-0 flex items-center justify-center z-50"
                >
                  <DepositModal onClose={closeDepositModal} />
                </motion.div>
              )}

              {isWithdrawOpen && (
                <motion.div
                  key="withdraw-modal"
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 20 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  className="fixed inset-0 flex items-center justify-center z-50"
                >
                  <WithdrawModal onClose={closeWithdrawModal} />
                </motion.div>
              )}

              {showLogin && (
                <motion.div
                  key="login-modal"
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 20 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  className="fixed inset-0 flex items-center justify-center z-50"
                >
                  <Login />
                </motion.div>
              )}

              {showRegister && (
                <motion.div
                  key="register-modal"
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 20 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  className="fixed inset-0 flex items-center justify-center z-50"
                >
                  <Register />
                </motion.div>
              )}
            </AnimatePresence>
          </Suspense>
        </div>
      </NavigationHandler>
    </Router>
  );
}

export default App;
// /************code-used-in-prod***with-Logout-Navigation-Logic******************************************************************************************************************************************************************************************00 */
// import React, { useState, useEffect, Suspense, lazy } from "react"; // Import Suspense and lazy
// import {
//   BrowserRouter as Router,
//   Routes,
//   Route,
//   useNavigate,
//   useLocation,
//   Navigate,
// } from "react-router-dom";
// import { useDispatch, useSelector } from "react-redux";
// import { AnimatePresence, motion } from "framer-motion";
// // Hooks
// import useBalanceRealtime from "./hooks/useBalanceRealtime";

// // Redux Actions/Thunks
// import { fetchJackpotPool } from "./features/roundSlice";
// import { logout } from "./features/authSlice"; // Assuming you have a logout action

// // Components (Core Layout & Eagerly Loaded)
// import Header from "./components/Header";
// import ProtectedRoute from "./components/ProtectedRoute";
// import ToastContainerWrapper from "./components/ToastContainerWrapper";
// import LoadingSpinner from "./components/LoadingSpinner"; // Assuming you have a loading component
// import LogoutConfirmModal from "./components/LogoutConfirmModal"; // Create this component for the confirmation
// import JackpotInfo from "./components/JackpotInfo";
// import HowToPlay from "./components/HowToPlay";
// import Faqs from "./components/Faqs";
// import TermsOfService from "./components/TermsOfService";
// import PrivacyPolicy from "./components/PrivacyPolicy";
// import ResponsibleGaming from "./components/ResponsibleGaming";
// import { useDepositModal, useShowLogin, useShowRegister } from "./Context/modalContex";
// import GameLayout from "./components/GameLayout";
// import ContactBar from "./components/ContactBar";
// import Footer from "./components/Footer";
// // import AgeVerification from "./components/AgeVerification";

// // Lazy-loaded Route Components
// const GameRoom = lazy(() => import("./components/GameRoom"));
// const Profile = lazy(() => import("./components/Profile"));
// const Login = lazy(() => import("./components/Login"));
// const Register = lazy(() => import("./components/Register"));
// const ReferalRegister = lazy(() => import("./components/ReferalRegister"));

// // Lazy-loaded Modal Components
// const DepositModal = lazy(() => import("./components/DepositModal"));
// const WithdrawModal = lazy(() => import("./components/WithdrawModal"));

// // NavigationHandler component to manage back navigation and logout confirmation
// /*************************************************************************************************************************************** */

// // const NavigationHandler = ({ children }) => {
// //   const navigate = useNavigate();
// //   const location = useLocation();
// //   const dispatch = useDispatch();
// //   const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
// //   const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
// //   const [pendingNavigation, setPendingNavigation] = useState(null);

// //   // Track history to detect back navigation
// //   useEffect(() => {
// //     const handlePopState = (event) => {
// //       // Only show logout confirmation if user is authenticated and trying to go back from
// //       // a protected route to login/register or public routes
// //       if (isAuthenticated &&
// //          (location.pathname === '/profile' || location.pathname.includes('/game/')) &&
// //          !event.state?.bypass) {

// //         // Prevent the default back navigation
// //         event.preventDefault();
// //         setPendingNavigation('/'); // Default to homepage
// //         setShowLogoutConfirm(true);
// //         return;
// //       }
// //     };

// //     window.addEventListener('popstate', handlePopState);
// //     return () => window.removeEventListener('popstate', handlePopState);
// //   }, [isAuthenticated, location.pathname]);

// //   const handleConfirmLogout = () => {
// //     dispatch(logout());
// //     navigate('/', { state: { bypass: true } });
// //     setShowLogoutConfirm(false);
// //   };

// //   const handleCancelLogout = () => {
// //     setShowLogoutConfirm(false);
// //     // Push the current path again to reset the browser history stack
// //     navigate(location.pathname, { replace: true });
// //   };

// //   return (
// //     <>
// //       {children}
// //       {showLogoutConfirm && (
// //         <LogoutConfirmModal
// //           isOpen={showLogoutConfirm}
// //           onConfirm={handleConfirmLogout}
// //           onCancel={handleCancelLogout}
// //         />
// //       )}
// //     </>
// //   );
// // };
// /******************************************************************************************************************************* */

// // NavigationHandler component to manage back navigation and logout confirmation
// const NavigationHandler = ({ children }) => {
//   const navigate = useNavigate();
//   const location = useLocation();
//   const dispatch = useDispatch();
//   const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
//   const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

//   // Track history to detect back navigation
//   useEffect(() => {
//     const handlePopState = (event) => {
//       // Only show logout confirmation if user is authenticated and trying to go back from
//       // a protected route to login/register or public routes
//       if (
//         isAuthenticated &&
//         (location.pathname === "/profile" ||
//           location.pathname.includes("/game/")) &&
//         !event.state?.bypass
//       ) {
//         // Prevent the default back navigation
//         window.history.pushState(null, "", location.pathname);
//         setShowLogoutConfirm(true);
//         return;
//       }
//     };

//     window.addEventListener("popstate", handlePopState);
//     return () => window.removeEventListener("popstate", handlePopState);
//   }, [isAuthenticated, location.pathname]);

//   const handleConfirmLogout = () => {
//     // Dispatch logout action to clear user credentials
//     dispatch(logout());

//     // Close the modal
//     setShowLogoutConfirm(false);

//     // Navigate back to the game room with bypass flag to prevent re-triggering confirmation
//     navigate("/", { state: { bypass: true }, replace: true });
//   };

//   const handleCancelLogout = () => {
//     setShowLogoutConfirm(false);
//     // Push the current path again to reset the browser history stack
//     window.history.pushState(null, "", location.pathname);
//   };

//   return (
//     <>
//       {children}
//       {showLogoutConfirm && (
//         <LogoutConfirmModal
//           isOpen={showLogoutConfirm}
//           onConfirm={handleConfirmLogout}
//           onCancel={handleCancelLogout}
//         />
//       )}
//     </>
//   );
// };
// function App() {
//   const dispatch = useDispatch();
//   // Custom hook likely sets up listeners or fetches real-time balance updates
//   useBalanceRealtime();
//   const { showLogin } = useShowLogin();
//   const { showRegister } = useShowRegister();
//   const { showDepositModal, setShowDepositModal } = useDepositModal();

//   // State for controlling modal visibility
//   const [isWithdrawOpen, setWithdrawOpen] = useState(false);

//   // Fetch initial application data on component mount
//   useEffect(() => {
//     // Fetch the current jackpot amount. Error handling should be managed within the thunk/slice.
//     dispatch(fetchJackpotPool());

//     // Consider fetching other essential initial data here if needed.
//     // Example: dispatch(fetchUserProfile());
//   }, [dispatch]); // dispatch is stable, effect runs once on mount

//   const openDepositModal = () => setShowDepositModal(true);
//   const closeDepositModal = () => setShowDepositModal(false);
//   const openWithdrawModal = () => setWithdrawOpen(true);
//   const closeWithdrawModal = () => setWithdrawOpen(false);

//   return (
//     <Router>
//       {/* Wrap everything in the NavigationHandler */}
//       <NavigationHandler>
//         {/* Global Toast Container */}
//         <ToastContainerWrapper />

//         <div className="min-h-screen bg-[#071129] text-gray-100">
//           {/**bg-color -#091622 */}
//           {/* Added default text color */}
//           <Header
//             onDepositOpen={openDepositModal}
//             onWithdrawOpen={openWithdrawModal}
//           />
//           <main className="container md:max-w-full mx-auto p-1 mt-20 md:mt-0 md:p-6">
//             {/* Suspense handles the loading state for lazy-loaded components */}
//             <Suspense fallback={<LoadingSpinner />}>
//               {" "}
//               {/* Replace LoadingSpinner with your actual loading indicator */}
//               <Routes>
//                 <Route path="/" element={<GameLayout />}>
//                   {/* Public Routes */}
//                   <Route index element={<GameRoom />} />
//                   {/* <Route path="/login" element={<Login />} /> */}
//                   <Route path="/register" element={<ReferalRegister />} />
//                   <Route path="/how" element={<HowToPlay />} />
//                   <Route path="/faqs" element={<Faqs />} />
//                   <Route path="/terms" element={<TermsOfService />} />
//                   <Route path="/privacy" element={<PrivacyPolicy />} />
//                   <Route path="/responsible-gaming" element={<ResponsibleGaming />} />
//                   <Route path="*" element={<Navigate to="/" replace />} />
               
               
                  

//                   {/* Protected Routes */}
//                   <Route
//                     path="/profile"
//                     element={
//                       <ProtectedRoute>
//                         <Profile />
//                       </ProtectedRoute>
//                     }
//                   />

//                   {/* <Route path="/jackpotInfo" element={<JackpotInfo />} /> */}
//                   {/* Add other routes as needed */}
//                 </Route>
//               </Routes>
//             </Suspense>
//           </main>
//           {/* Modern Footer */}
//           <Footer />
//           {/* Conditionally render modals using Suspense */}
//           <Suspense fallback={<LoadingSpinner/>}>
//             <AnimatePresence mode="wait">
//               {showDepositModal && (
//                 <motion.div
//                   key="deposit-modal"
//                   initial={{ opacity: 0, scale: 0.95, y: 20 }}
//                   animate={{ opacity: 1, scale: 1, y: 0 }}
//                   exit={{ opacity: 0, scale: 0.95, y: 20 }}
//                   transition={{ duration: 0.3, ease: "easeOut" }}
//                   className="fixed inset-0 flex items-center justify-center z-50"
//                 >
//                   <DepositModal onClose={closeDepositModal} />
//                 </motion.div>
//               )}

//               {isWithdrawOpen && (
//                 <motion.div
//                   key="withdraw-modal"
//                   initial={{ opacity: 0, scale: 0.95, y: 20 }}
//                   animate={{ opacity: 1, scale: 1, y: 0 }}
//                   exit={{ opacity: 0, scale: 0.95, y: 20 }}
//                   transition={{ duration: 0.3, ease: "easeOut" }}
//                   className="fixed inset-0 flex items-center justify-center z-50"
//                 >
//                   <WithdrawModal onClose={closeWithdrawModal} />
//                 </motion.div>
//               )}

//               {showLogin && (
//                 <motion.div
//                   key="login-modal"
//                   initial={{ opacity: 0, scale: 0.95, y: 20 }}
//                   animate={{ opacity: 1, scale: 1, y: 0 }}
//                   exit={{ opacity: 0, scale: 0.95, y: 20 }}
//                   transition={{ duration: 0.3, ease: "easeOut" }}
//                   className="fixed inset-0 flex items-center justify-center z-50"
//                 >
//                   <Login />
//                 </motion.div>
//               )}

//               {showRegister && (
//                 <motion.div
//                   key="register-modal"
//                   initial={{ opacity: 0, scale: 0.95, y: 20 }}
//                   animate={{ opacity: 1, scale: 1, y: 0 }}
//                   exit={{ opacity: 0, scale: 0.95, y: 20 }}
//                   transition={{ duration: 0.3, ease: "easeOut" }}
//                   className="fixed inset-0 flex items-center justify-center z-50"
//                 >
//                   <Register />
//                 </motion.div>
//               )}
//             </AnimatePresence>
//           </Suspense>
//           {/* <Suspense fallback={<p>Loading...</p>}>

// {isDepositOpen && <DepositModal onClose={closeDepositModal} />}
// {isWithdrawOpen && <WithdrawModal onClose={closeWithdrawModal} />}
// {showLogin && <Login />}
// {showRegister && <Register />}
// </Suspense> */}
//         </div>
//       </NavigationHandler>
//     </Router>
//   );
// }

// export default App;

///***ogCode*********************************ogCode**********************************************************************ogCode**************************************************************************************************************************************************************************************************************************************************************************************************************************************************** */

// // src/App.js
// import React, { useState, useEffect, Suspense, lazy } from "react"; // Import Suspense and lazy
// import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
// import { useDispatch } from "react-redux";

// // Hooks
// import useBalanceRealtime from "./hooks/useBalanceRealtime";

// // Redux Actions/Thunks
// import { fetchJackpotPool } from "./features/roundSlice";

// // Components (Core Layout & Eagerly Loaded)
// import Header from "./components/Header";
// import ProtectedRoute from "./components/ProtectedRoute";
// import ToastContainerWrapper from "./components/ToastContainerWrapper";
// import LoadingSpinner from "./components/LoadingSpinner"; // Assuming you have a loading component

// // Lazy-loaded Route Components
// const GameRoom = lazy(() => import("./components/GameRoom"));
// const Profile = lazy(() => import("./components/Profile"));
// const Login = lazy(() => import("./components/Login"));
// const Register = lazy(() => import("./components/Register"));

// // Lazy-loaded Modal Components (Optional, but can be good if they are large)
// const DepositModal = lazy(() => import("./components/DepositModal"));
// const WithdrawModal = lazy(() => import("./components/WithdrawModal"));

// // Optional: Import custom toast styles if moved to a separate file
// // import './ToastStyles.css';

// function App() {
//   const dispatch = useDispatch();
//   // Custom hook likely sets up listeners or fetches real-time balance updates
//   useBalanceRealtime();

//   // State for controlling modal visibility
//   const [isDepositOpen, setDepositOpen] = useState(false);
//   const [isWithdrawOpen, setWithdrawOpen] = useState(false);

//   // Fetch initial application data on component mount
//   useEffect(() => {
//     // Fetch the current jackpot amount. Error handling should be managed within the thunk/slice.
//     dispatch(fetchJackpotPool());

//     // Consider fetching other essential initial data here if needed.
//     // Example: dispatch(fetchUserProfile());
//   }, [dispatch]); // dispatch is stable, effect runs once on mount

//   const openDepositModal = () => setDepositOpen(true);
//   const closeDepositModal = () => setDepositOpen(false);
//   const openWithdrawModal = () => setWithdrawOpen(true);
//   const closeWithdrawModal = () => setWithdrawOpen(false);

//   return (
//     <Router>
//       {/* Global Toast Container */}
//       <ToastContainerWrapper />

//       <div className="min-h-screen bg-[#091622] text-gray-100"> {/* Added default text color */}
//         <Header
//           onDepositOpen={openDepositModal}
//           onWithdrawOpen={openWithdrawModal}
//         />

//         <main className="container md:max-w-full mx-auto p-1 mt-10 md:mt-0 md:p-6">
//           {/* Suspense handles the loading state for lazy-loaded components */}
//           <Suspense fallback={<LoadingSpinner />}> {/* Replace LoadingSpinner with your actual loading indicator */}
//             <Routes>
//               {/* Public Routes */}
//               <Route path="/" element={<GameRoom />} />
//               <Route path="/login" element={<Login />} />
//               <Route path="/register" element={<Register />} />

//               {/* Protected Routes */}
//               <Route
//                 path="/profile"
//                 element={
//                   <ProtectedRoute>
//                     <Profile />
//                   </ProtectedRoute>
//                 }
//               />
//               {/* Add other routes as needed */}
//             </Routes>
//           </Suspense>
//         </main>

//         {/* Conditionally render modals using Suspense */}
//         <Suspense fallback={null}> {/* Modals likely don't need a heavy spinner */}
//           {isDepositOpen && <DepositModal onClose={closeDepositModal} />}
//           {isWithdrawOpen && <WithdrawModal onClose={closeWithdrawModal} />}
//         </Suspense>
//       </div>
//     </Router>
//   );
// }

// export default App;

//************************************************************************************************************************************************************************************************************************************************************************************************************************************************************************************************************************************************************* */

// import React, { useState } from "react";
// import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
// import useBalanceRealtime from "./hooks/useBalanceRealtime";

// // Import components
// import Header from "./components/Header";
// import GameRoom from "./components/GameRoom";
// import Profile from "./components/Profile";
// import Login from "./components/Login";
// import Register from "./components/Register";
// import ProtectedRoute from "./components/ProtectedRoute";
// import DepositModal from "./components/DepositModal";
// import WithdrawModal from "./components/WithdrawModal";
// import ToastContainerWrapper from "./components/ToastContainerWrapper"; // Import the wrapper

// // Optional: Import custom toast styles if moved to a separate file
// // import './ToastStyles.css';

// function App() {
//   useBalanceRealtime();
//   const [isDepositOpen, setDepositOpen] = useState(false);
//   const [isWithdrawOpen, setWithdrawOpen] = useState(false);

//   return (
//     <Router>
//       {/* Render ToastContainerWrapper once here */}
//       <ToastContainerWrapper />
//       <div className="min-h-screen bg-[#091622]">
//         <Header
//           onDepositOpen={() => setDepositOpen(true)}
//           onWithdrawOpen={() => setWithdrawOpen(true)}
//         />
//         <main className="container md:max-w-full mx-auto p-1 mt-10 md:mt-0 md:p-6">
//           <Routes>
//             {/* Public GameRoom route */}
//             <Route path="/" element={<GameRoom />} />
//             <Route
//               path="/profile"
//               element={
//                 <ProtectedRoute>
//                   <Profile />
//                 </ProtectedRoute>
//               }
//             />
//             <Route path="/login" element={<Login />} />
//             <Route path="/register" element={<Register />} />
//           </Routes>
//         </main>
//         {isDepositOpen && (
//           <DepositModal onClose={() => setDepositOpen(false)} />
//         )}
//         {isWithdrawOpen && (
//           <WithdrawModal onClose={() => setWithdrawOpen(false)} />
//         )}
//       </div>
//     </Router>
//   );
// }

// export default App;

// import React, { useState } from "react";
// import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
// import useBalanceRealtime from "./hooks/useBalanceRealtime";

// // Import components
// import Header from "./components/Header";
// import GameRoom from "./components/GameRoom";
// import Profile from "./components/Profile";
// import Login from "./components/Login";
// import Register from "./components/Register";
// import ProtectedRoute from "./components/ProtectedRoute";
// import DepositModal from "./components/DepositModal";
// import WithdrawModal from "./components/WithdrawModal";

// function App() {
//   useBalanceRealtime();
//   const [isDepositOpen, setDepositOpen] = useState(false);
//   const [isWithdrawOpen, setWithdrawOpen] = useState(false);

//   return (
//     <Router>
//       <div className="min-h-screen bg-[#091622]">
//         <Header
//           onDepositOpen={() => setDepositOpen(true)}
//           onWithdrawOpen={() => setWithdrawOpen(true)}
//         />
//         <main className="container md:max-w-full mx-auto p-1 mt-10 md:mt-0 md:p-6">
//           <Routes>
//             {/* Public GameRoom route */}
//             <Route path="/" element={<GameRoom />} />
//             <Route
//               path="/profile"
//               element={
//                 <ProtectedRoute>
//                   <Profile />
//                 </ProtectedRoute>
//               }
//             />
//             <Route path="/login" element={<Login />} />
//             <Route path="/register" element={<Register />} />
//           </Routes>
//         </main>
//         {isDepositOpen && (
//           <DepositModal onClose={() => setDepositOpen(false)} />
//         )}
//         {isWithdrawOpen && (
//           <WithdrawModal onClose={() => setWithdrawOpen(false)} />
//         )}
//       </div>
//     </Router>
//   );
// }

// export default App;

// import React, { useState } from "react";
// import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
// import useBalanceRealtime from "./hooks/useBalanceRealtime";

// // Import components
// import Header from "./components/Header";
// import GameRoom from "./components/GameRoom";
// import Profile from "./components/Profile";
// import Login from "./components/Login";
// import Register from "./components/Register";
// import ProtectedRoute from "./components/ProtectedRoute";
// import DepositModal from "./components/DepositModal";
// import WithdrawModal from "./components/WithdrawModal";

// function App() {
//   useBalanceRealtime();
//   const [isDepositOpen, setDepositOpen] = useState(false);
//   const [isWithdrawOpen, setWithdrawOpen] = useState(false);

//   return (
//     <Router>
//       <div className="min-h-screen bg-[#091622]">
//         <Header
//           onDepositOpen={() => setDepositOpen(true)}
//           onWithdrawOpen={() => setWithdrawOpen(true)}
//         />
//         <main className="container md:max-w-full mx-auto p-1 mt-10 md:mt-0 md:p-6">
//           <Routes>
//             <Route path="/test" element={<GameRoom />} />
//             <Route
//               path="/"
//               element={
//                 <ProtectedRoute>
//                   <GameRoom />
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/profile"
//               element={
//                 <ProtectedRoute>
//                   <Profile />
//                 </ProtectedRoute>
//               }
//             />
//             <Route path="/login" element={<Login />} />
//             <Route path="/register" element={<Register />} />
//           </Routes>
//         </main>
//         {isDepositOpen && (
//           <DepositModal onClose={() => setDepositOpen(false)} />
//         )}
//         {isWithdrawOpen && (
//           <WithdrawModal onClose={() => setWithdrawOpen(false)} />
//         )}
//       </div>
//     </Router>
//   );
// }

// export default App;

// import React, {useEffect, useState } from "react";
// import {
//   BrowserRouter as Router,
//   Routes,
//   Route,
//   Link,
//   useNavigate,
// } from "react-router-dom";
// import { useDispatch, useSelector } from "react-redux";
// import { performLogout, logout } from "./features/authSlice";
// import useBalanceRealtime from "./hooks/useBalanceRealtime";
// import axiosInstance from "./app/axiosInstance";
// import { persistor } from "./app/store";

// // Components
// import GameRoom from "./components/GameRoom";
// import Profile from "./components/Profile";
// import Login from "./components/Login";
// import Register from "./components/Register";
// import ProtectedRoute from "./components/ProtectedRoute";
// import DepositModal from "./components/DepositModal";
// import WithdrawModal from "./components/WithdrawModal";

// function Header({ onDepositOpen, onWithdrawOpen }) {
//   const dispatch = useDispatch();
//   const { user, accessToken } = useSelector((state) => state.auth);
//   const navigate = useNavigate();
//   const [menuOpen, setMenuOpen] = useState(false);
//   const [visible, setVisible] = useState(true);
//   const [lastScrollY, setLastScrollY] = useState(0);

//   const handleScroll = () => {
//     const currentScrollY = window.scrollY;

//     // If scrolling down and past a threshold (e.g., 100px), hide the header
//     if (currentScrollY > lastScrollY && currentScrollY > 40) {
//       setVisible(false);
//     }
//     // If scrolling up, show the header
//     else if (currentScrollY < lastScrollY ) {
//       setVisible(true);
//     }

//     setLastScrollY(currentScrollY);
//   };

//   useEffect(() => {
//     window.addEventListener('scroll', handleScroll, { passive: true });

//     return () => {
//       window.removeEventListener('scroll', handleScroll);
//     };
//   }, [lastScrollY]);

//   const handleLogout = () => {
//     dispatch(performLogout())
//       .unwrap()
//       .then(() => {
//         dispatch(logout());
//         persistor.purge().then(() => {
//           localStorage.removeItem("persist:root");
//           axiosInstance.defaults.headers.common["Authorization"] = "";
//           navigate("/login");
//         });
//       })
//       .catch((err) => console.error("Logout failed:", err));
//   };

//   // Smooth scroll to top function
//   const scrollToTop = (e) => {
//     e.preventDefault();
//     window.scrollTo({
//       top: 0,
//       behavior: 'smooth'
//     });
//     if (window.location.pathname !== '/') {
//       // After scroll animation starts, navigate to home
//       setTimeout(() => {
//         navigate('/');
//       }, 100);
//     }
//   };

//   return (
//     <header
//       className={`bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white border-b-2 border-green-900 shadow-lg fixed top-0 left-0 right-0 z-50 rounded-b-xl transition-transform duration-300 ${
//         visible ? 'transform translate-y-0' : 'transform -translate-y-full'
//       }`}
//     >
//       {/* Top Bar */}
//       <div className="container mx-auto flex items-center justify-between px-4 py-3">
//         {/* Left: Logo & User Actions */}
//         <div className="flex items-center space-x-4">
//           <a
//             href="/"
//             onClick={scrollToTop}
//             className="font-bold text-xl text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-blue-500 hover:from-blue-500 hover:to-green-400 transition-all duration-300 flex items-center">
//             Game Room
//           </a>
//           {accessToken && (
//             <div className="flex items-center space-x-3">
//               <button
//                 onClick={onDepositOpen}
//                 className="px-3 py-1.5 bg-gradient-to-r from-green-500 to-emerald-600 rounded-md hover:from-green-600 hover:to-emerald-700 transition-all duration-300 text-sm font-medium flex items-center shadow-md shadow-green-900/20 hover:shadow-lg hover:shadow-green-900/30"
//                 aria-label="Open deposit modal">
//                 <svg
//                   className="w-4 h-4 mr-1"
//                   fill="none"
//                   stroke="currentColor"
//                   viewBox="0 0 24 24"
//                   xmlns="http://www.w3.org/2000/svg">
//                   <path
//                     strokeLinecap="round"
//                     strokeLinejoin="round"
//                     strokeWidth="2"
//                     d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
//                 </svg>
//                 Deposit
//               </button>
//               <div className="text-sm bg-gray-800/80 backdrop-blur-sm px-3 py-1.5 rounded-md border border-purple-500/20 flex items-center shadow-inner shadow-purple-900/10 group hover:border-purple-500/40 transition-all duration-300">
//                 <svg
//                   className="w-4 h-4 mr-1 text-green-400 group-hover:text-green-300 group-hover:rotate-12 transition-all duration-300"
//                   fill="none"
//                   stroke="currentColor"
//                   viewBox="0 0 24 24"
//                   xmlns="http://www.w3.org/2000/svg">
//                   <path
//                     strokeLinecap="round"
//                     strokeLinejoin="round"
//                     strokeWidth="2"
//                     d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
//                 </svg>
//                 <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-300 font-medium">
//                   Ksh{user ? Number(user.balance).toFixed(2) : "0.00"}
//                 </span>
//               </div>
//             </div>
//           )}
//         </div>

//         {/* Right: Navigation */}
//         <div className="flex items-center">
//           {/* Desktop Nav */}
//           <nav className="hidden md:flex items-center space-x-4">
//             <a
//               href="/"
//               onClick={scrollToTop}
//               className="relative px-2 py-1 group">
//               <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-green-400 to-blue-500 group-hover:w-full transition-all duration-300"></span>
//               <span className="group-hover:text-blue-400 transition-colors duration-150">
//                 Games
//               </span>
//             </a>
//             <Link
//               to="/profile"
//               className="relative px-2 py-1 group">
//               <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-green-400 to-blue-500 group-hover:w-full transition-all duration-300"></span>
//               <span className="group-hover:text-blue-400 transition-colors duration-150">
//                 Profile
//               </span>
//             </Link>
//             {accessToken ? (
//               <>
//                 <button
//                   onClick={onWithdrawOpen}
//                   className="px-3 py-1.5 bg-gray-800/80 backdrop-blur-sm border border-red-500/20 text-red-400 rounded-md hover:bg-gray-700 hover:border-red-500/40 transition-all duration-300 text-sm font-medium flex items-center"
//                   aria-label="Open withdraw modal">
//                   <svg
//                     className="w-4 h-4 mr-1"
//                     fill="none"
//                     stroke="currentColor"
//                     viewBox="0 0 24 24"
//                     xmlns="http://www.w3.org/2000/svg">
//                     <path
//                       strokeLinecap="round"
//                       strokeLinejoin="round"
//                       strokeWidth="2"
//                       d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
//                     <path
//                       strokeLinecap="round"
//                       strokeLinejoin="round"
//                       strokeWidth="2"
//                       d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
//                   </svg>
//                   Withdraw
//                 </button>
//                 <button
//                   onClick={handleLogout}
//                   className="px-3 py-1.5 bg-gray-800/80 backdrop-blur-sm border border-gray-700 rounded-md hover:bg-gray-700 hover:border-gray-600 transition-all duration-300 text-sm font-medium flex items-center"
//                   aria-label="Logout">
//                   <svg
//                     className="w-4 h-4 mr-1"
//                     fill="none"
//                     stroke="currentColor"
//                     viewBox="0 0 24 24"
//                     xmlns="http://www.w3.org/2000/svg">
//                     <path
//                       strokeLinecap="round"
//                       strokeLinejoin="round"
//                       strokeWidth="2"
//                       d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
//                   </svg>
//                   Logout
//                 </button>
//                 <div className="text-sm bg-gray-800/80 backdrop-blur-sm px-3 py-1.5 rounded-md border border-purple-500/20 flex items-center group hover:border-purple-500/40 transition-all duration-300">
//                   <svg
//                     className="w-4 h-4 mr-1 text-purple-400 group-hover:text-purple-300 transition-all duration-300"
//                     fill="none"
//                     stroke="currentColor"
//                     viewBox="0 0 24 24"
//                     xmlns="http://www.w3.org/2000/svg">
//                     <path
//                       strokeLinecap="round"
//                       strokeLinejoin="round"
//                       strokeWidth="2"
//                       d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
//                   </svg>
//                   <span className="group-hover:text-purple-300 transition-all duration-300">
//                     {user?.username}
//                   </span>
//                 </div>
//               </>
//             ) : (
//               <>
//                 <Link
//                   to="/login"
//                   className="px-3 py-1.5 bg-gray-800/80 backdrop-blur-sm border border-blue-500/20 rounded-md hover:bg-gray-700 hover:border-blue-500/40 transition-all duration-300 text-sm font-medium flex items-center">
//                   <svg
//                     className="w-4 h-4 mr-1"
//                     fill="none"
//                     stroke="currentColor"
//                     viewBox="0 0 24 24"
//                     xmlns="http://www.w3.org/2000/svg">
//                     <path
//                       strokeLinecap="round"
//                       strokeLinejoin="round"
//                       strokeWidth="2"
//                       d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"></path>
//                   </svg>
//                   Login
//                 </Link>
//                 <Link
//                   to="/register"
//                   className="px-3 py-1.5 bg-gradient-to-r from-green-500 to-blue-600 rounded-md hover:from-green-600 hover:to-blue-700 transition-all duration-300 text-sm font-medium flex items-center shadow-md shadow-blue-900/20 hover:shadow-lg hover:shadow-blue-900/30">
//                   <svg
//                     className="w-4 h-4 mr-1"
//                     fill="none"
//                     stroke="currentColor"
//                     viewBox="0 0 24 24"
//                     xmlns="http://www.w3.org/2000/svg">
//                     <path
//                       strokeLinecap="round"
//                       strokeLinejoin="round"
//                       strokeWidth="2"
//                       d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"></path>
//                   </svg>
//                   Register
//                 </Link>
//               </>
//             )}
//           </nav>

//           {/* Mobile Hamburger */}
//           <button
//             onClick={() => setMenuOpen(!menuOpen)}
//             className="md:hidden p-2 rounded-md mx-1 hover:bg-gray-800/80 backdrop-blur-sm transition-all duration-150 group"
//             aria-label="Toggle mobile menu"
//             aria-expanded={menuOpen}>
//             <svg
//               className="h-6 w-6 group-hover:text-blue-400 transition-all duration-300"
//               fill="none"
//               stroke="currentColor"
//               viewBox="0 0 24 24">
//               <path
//                 strokeLinecap="round"
//                 strokeLinejoin="round"
//                 strokeWidth="2"
//                 d={menuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16m-7 6h7"}
//               />
//             </svg>
//           </button>
//         </div>
//       </div>

//       {/* Mobile Menu */}
//       {menuOpen && (
//         <nav className="md:hidden bg-gray-800/95 backdrop-blur-sm border-t border-purple-500/20 px-4 py-4 animate-fade-in-down shadow-lg">
//           <div className="flex flex-col space-y-3 py-2">
//             <a
//               href="/"
//               onClick={(e) => {
//                 scrollToTop(e);
//                 setMenuOpen(false);
//               }}
//               className="flex items-center text-white hover:text-blue-400 py-2 px-3 hover:bg-gray-700/70 rounded-md transition-all duration-300">
//               <svg
//                 className="w-5 h-5 mr-2 text-green-400"
//                 fill="none"
//                 stroke="currentColor"
//                 viewBox="0 0 24 24"
//                 xmlns="http://www.w3.org/2000/svg">
//                 <path
//                   strokeLinecap="round"
//                   strokeLinejoin="round"
//                   strokeWidth="2"
//                   d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"></path>
//               </svg>
//               Games
//             </a>
//             <Link
//               to="/profile"
//               className="flex items-center text-white hover:text-blue-400 py-2 px-3 hover:bg-gray-700/70 rounded-md transition-all duration-300"
//               onClick={() => setMenuOpen(false)}>
//               <svg
//                 className="w-5 h-5 mr-2 text-green-400"
//                 fill="none"
//                 stroke="currentColor"
//                 viewBox="0 0 24 24"
//                 xmlns="http://www.w3.org/2000/svg">
//                 <path
//                   strokeLinecap="round"
//                   strokeLinejoin="round"
//                   strokeWidth="2"
//                   d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
//               </svg>
//               Profile
//             </Link>

//             {accessToken ? (
//               <>
//                 <div className="bg-gray-900/80 backdrop-blur-sm rounded-md p-3 mb-2 flex items-center justify-between border border-purple-500/20">
//                   <div className="flex items-center">
//                     <svg
//                       className="w-5 h-5 mr-2 text-green-400"
//                       fill="none"
//                       stroke="currentColor"
//                       viewBox="0 0 24 24"
//                       xmlns="http://www.w3.org/2000/svg">
//                       <path
//                         strokeLinecap="round"
//                         strokeLinejoin="round"
//                         strokeWidth="2"
//                         d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
//                     </svg>
//                     <span className="text-sm">Balance:</span>
//                   </div>
//                   <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-300 font-medium">
//                     ${user ? Number(user.balance).toFixed(2) : "0.00"}
//                   </span>
//                 </div>

//                 <div className="grid grid-cols-2 gap-3 mb-2">
//                   <button
//                     onClick={() => {
//                       onDepositOpen();
//                       setMenuOpen(false);
//                     }}
//                     className="flex items-center justify-center px-3 py-3 bg-gradient-to-r from-green-500 to-emerald-600 rounded-md hover:from-green-600 hover:to-emerald-700 transition-all duration-300 text-sm font-medium shadow-md shadow-green-900/20">
//                     <svg
//                       className="w-4 h-4 mr-2"
//                       fill="none"
//                       stroke="currentColor"
//                       viewBox="0 0 24 24"
//                       xmlns="http://www.w3.org/2000/svg">
//                       <path
//                         strokeLinecap="round"
//                         strokeLinejoin="round"
//                         strokeWidth="2"
//                         d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
//                     </svg>
//                     Deposit
//                   </button>

//                   <button
//                     onClick={() => {
//                       onWithdrawOpen();
//                       setMenuOpen(false);
//                     }}
//                     className="flex items-center justify-center px-3 py-3 bg-gray-800/80 backdrop-blur-sm border border-red-500/30 text-red-400 rounded-md hover:bg-gray-700 hover:border-red-500/50 transition-all duration-300 text-sm font-medium">
//                     <svg
//                       className="w-4 h-4 mr-2"
//                       fill="none"
//                       stroke="currentColor"
//                       viewBox="0 0 24 24"
//                       xmlns="http://www.w3.org/2000/svg">
//                       <path
//                         strokeLinecap="round"
//                         strokeLinejoin="round"
//                         strokeWidth="2"
//                         d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
//                       <path
//                         strokeLinecap="round"
//                         strokeLinejoin="round"
//                         strokeWidth="2"
//                         d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
//                     </svg>
//                     Withdraw
//                   </button>
//                 </div>

//                 <div className="flex items-center justify-between bg-gray-900/80 backdrop-blur-sm rounded-md p-3 mb-2 border border-purple-500/20">
//                   <div className="flex items-center">
//                     <svg
//                       className="w-5 h-5 mr-2 text-purple-400"
//                       fill="none"
//                       stroke="currentColor"
//                       viewBox="0 0 24 24"
//                       xmlns="http://www.w3.org/2000/svg">
//                       <path
//                         strokeLinecap="round"
//                         strokeLinejoin="round"
//                         strokeWidth="2"
//                         d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
//                     </svg>
//                     <span className="text-sm">User:</span>
//                   </div>
//                   <span className="text-purple-300">{user?.username}</span>
//                 </div>

//                 <button
//                   onClick={() => {
//                     handleLogout();
//                     setMenuOpen(false);
//                   }}
//                   className="flex items-center justify-center w-full px-3 py-3 bg-gray-800/80 backdrop-blur-sm border border-gray-700 rounded-md hover:bg-gray-700 hover:border-gray-600 transition-all duration-300 text-sm font-medium">
//                   <svg
//                     className="w-4 h-4 mr-2"
//                     fill="none"
//                     stroke="currentColor"
//                     viewBox="0 0 24 24"
//                     xmlns="http://www.w3.org/2000/svg">
//                     <path
//                       strokeLinecap="round"
//                       strokeLinejoin="round"
//                       strokeWidth="2"
//                       d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
//                   </svg>
//                   Logout
//                 </button>
//               </>
//             ) : (
//               <>
//                 <Link
//                   to="/login"
//                   className="flex items-center justify-center px-3 py-3 bg-gray-800/80 backdrop-blur-sm border border-blue-500/20 rounded-md hover:bg-gray-700 hover:border-blue-500/40 transition-all duration-300 text-sm font-medium"
//                   onClick={() => setMenuOpen(false)}>
//                   <svg
//                     className="w-4 h-4 mr-2"
//                     fill="none"
//                     stroke="currentColor"
//                     viewBox="0 0 24 24"
//                     xmlns="http://www.w3.org/2000/svg">
//                     <path
//                       strokeLinecap="round"
//                       strokeLinejoin="round"
//                       strokeWidth="2"
//                       d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"></path>
//                   </svg>
//                   Login
//                 </Link>
//                 <Link
//                   to="/register"
//                   className="flex items-center justify-center px-3 py-3 bg-gradient-to-r from-green-500 to-blue-600 rounded-md hover:from-green-600 hover:to-blue-700 transition-all duration-300 text-sm font-medium shadow-md shadow-blue-900/20"
//                   onClick={() => setMenuOpen(false)}>
//                   <svg
//                     className="w-4 h-4 mr-2"
//                     fill="none"
//                     stroke="currentColor"
//                     viewBox="0 0 24 24"
//                     xmlns="http://www.w3.org/2000/svg">
//                     <path
//                       strokeLinecap="round"
//                       strokeLinejoin="round"
//                       strokeWidth="2"
//                       d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"></path>
//                   </svg>
//                   Register
//                 </Link>
//               </>
//             )}
//           </div>
//         </nav>
//       )}
//     </header>
//   );
// }

// function App() {
//   useBalanceRealtime();
//   const [isDepositOpen, setDepositOpen] = useState(false);
//   const [isWithdrawOpen, setWithdrawOpen] = useState(false);

//   return (
//     <Router>
//       <div className="min-h-screen bg-[#091622]">
//         <Header
//           onDepositOpen={() => setDepositOpen(true)}
//           onWithdrawOpen={() => setWithdrawOpen(true)}
//         />
//         <main className="container md:max-w-full mx-auto p-1 mt-10 md:mt-0 md:p-6">
//           <Routes>
//             <Route path="/test" element={
//                   <GameRoom />

//             }/>
//             <Route
//               path="/"
//               element={
//                 <ProtectedRoute>
//                   <GameRoom />
//                 </ProtectedRoute>
//               }
//             />

//             <Route
//               path="/profile"
//               element={
//                 <ProtectedRoute>
//                   <Profile />
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/login"
//               element={<Login />}
//             />
//             <Route
//               path="/register"
//               element={<Register />}
//             />
//           </Routes>
//         </main>
//         {isDepositOpen && (
//           <DepositModal onClose={() => setDepositOpen(false)} />
//         )}
//         {isWithdrawOpen && (
//           <WithdrawModal onClose={() => setWithdrawOpen(false)} />
//         )}
//       </div>
//     </Router>
//   );
// }

// export default App;

// // Add this to your CSS or Tailwind config for the animation
// const styles = `
//   @keyframes slide-in {
//     from { transform: translateY(-100%); opacity: 0; }
//     to { transform: translateY(0); opacity: 1; }
//   }
//   .animate-slide-in {
//     animation: slide-in 0.3s ease-out;
//   }
// `;
